import { Fragment, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import Head from 'next/head';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _reject from 'lodash/reject';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/error-message';
import NotFound from '../../components/not-found';
import { cityGroupsQuery, myGroupsQuery } from '../../apollo/groups';
import { buildTitle } from '../../utils/layout';
import GroupList from '../../components/group-list';
import MaterialLinkButton from '../../components/material-link-button';
import config from '../../config';
import Main from '../../components/main';
import { useAppContext } from '../../contexts/app';

const useStyles = makeStyles((theme) => ({
  groupsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(3, 0),
  },
  title: {
    margin: 0,
  },
  createButton: {
    margin: theme.spacing(2, 0, 0, 0),
  },
}));

const GroupsIndex = () => {
  const classes = useStyles();
  const { city: appCity, loggedInUser } = useAppContext();

  const {
    data: cityData,
    loading: cityLoading,
    error: cityError,
  } = useQuery(cityGroupsQuery, {
    variables: { slug: _get(appCity, 'slug') },
    skip: !appCity,
  });
  const city = appCity ? { ...appCity, ..._get(cityData, 'city') } : null;

  const {
    data: myGroupsData,
    loading: myGroupsLoading,
    //error: myGroupsError
  } = useQuery(myGroupsQuery, {
    ssr: false,
    skip: !loggedInUser,
  });
  const myGroups = _get(myGroupsData, 'me.groups');

  const cityGroups = useMemo(() => {
    if (!city) {
      return null;
    }
    if (!loggedInUser) {
      return _reject(city.groups, 'found');
    }
    return _reject(city.groups, (group) => {
      if (group.found) {
        return true;
      }
      const membership = _find(loggedInUser.groupMembers, (gm) => gm.group.id === group.id);
      return membership && _includes(['OWNER', 'MEMBER'], membership.role);
    });
  }, [loggedInUser, city]);

  const communityGroups = useMemo(() => {
    if (!city) {
      return null;
    }
    return _filter(city.groups, 'found');
  }, [city]);

  if (cityError) {
    return (
      <ErrorMessage
        message='An error occurred while loading groups.'
        error={cityError}
      />
    );
  }
  if (cityLoading) { return <Loading />; }
  if (!city) { return <NotFound />; }

  const pageTitle = `${city.name} Cycling Groups`;
  const canonicalUrl = `${config().universal.baseUri}/cities/${city.slug}/groups`;
  const metaDescription = `Join cycling community groups in ${city.name} and get upcoming event notifications.`;

  return (
    <Main signupBanner>
      <Head>
        <title>{buildTitle(pageTitle)}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="description" content={metaDescription} />
      </Head>

      <div className={classes.groupsHeader}>
        <Typography variant="h1" className={classes.title}>
          Groups
        </Typography>
        <MaterialLinkButton
          href={`/groups/new?city=${city.slug}`}
          color="primary"
          variant="contained"
        >
          Create Group
        </MaterialLinkButton>
      </div>

      {loggedInUser && (
        <Fragment>
          <Typography variant="h2">
            My Groups
          </Typography>
          <GroupList
            groups={myGroups}
            emptyMessage="You're not a member of any groups."
            loading={myGroupsLoading}
          />
        </Fragment>
      )}

      <Typography variant="h2">
        {city.name} Groups
      </Typography>
      <GroupList groups={cityGroups} loading={cityLoading} />

      {communityGroups && communityGroups.length > 0 && (
        <Fragment>
          <Typography variant="h2">
            Other {city.name} Groups
          </Typography>
          <GroupList groups={communityGroups} loading={cityLoading} />
        </Fragment>
      )}

      <MaterialLinkButton
        href={`/groups/new?city=${city.slug}`}
        color="primary"
        variant="contained"
        className={classes.createButton}
      >
        Create Group
      </MaterialLinkButton>
    </Main>
  );
};

export default GroupsIndex;
